import {
  OperationStatus,
  OperationStatusLabel,
} from "@/components/Dialog/helpers/DialogStatus";
import { useAuthorizedSheets } from "@/hooks/useAuthorizedCollections";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useAppRouteParams } from "../../../AppRoutes";
import { SaveType, postFileVersionDataCSV } from "../../../api/files";
import { getAPIErrorDetailOrMessage } from "../../../api/helpers";
import { getMaxConcurrentApiCount } from "../../../api/rateLimit";
import { Sheet } from "../../../domain/Sheet";
import { DragAndDropUploadData } from "../../DragAndDropFields";

type SheetCSVConnect = {
  fileId: string;
  sheetId: string;
  writeStatus: OperationStatus;
  error?: string;
};

export const useUploadSheetDataCSV = (
  uploadedFileList: DragAndDropUploadData[],
  setUploadedFileList: React.Dispatch<
    React.SetStateAction<DragAndDropUploadData[]>
  >,
  saveType: SaveType
) => {
  const { organizationId, versionId } = useAppRouteParams();

  const [csvSheetConnectList, setCSVSheetConnectList] = useState<
    SheetCSVConnect[]
  >([]);

  const [sheets] = useAuthorizedSheets(versionId);

  // ファイル名（{バージョン名}_{シート表示名}_data_{yyyyMMddHHmm}.csv）からシート名を抽出する関数
  const extractSheetNameFromFileName = useCallback(
    (fileName: string): string | null => {
      // 先頭: バージョン名（アンダースコア以外）
      // 中間: _{シート表示名}_data_
      // 末尾: 12桁日時 + .csv
      const match = fileName.match(/^[^_]+_(.+?)_data_\d{12}\.csv$/);
      return match ? match[1] : null;
    },
    []
  );

  const sortedSheets = useMemo(
    () =>
      [...sheets].sort((a, b) => {
        return Math.sign(a.sort - b.sort);
      }),
    [sheets]
  );

  // あるシートが、紐付け可能かを返す
  const isConnectableSheet = useCallback(
    (sheet: Sheet) => {
      // 参照シート・TADS参照シートを除外。
      if (sheet.referTo || sheet.tadReferTo) {
        return false;
      }
      // 紐付けられているファイルが存在しないものは紐付け可能
      return (
        csvSheetConnectList.find(({ sheetId }) => sheetId === sheet.id) ===
        undefined
      );
    },
    [csvSheetConnectList]
  );

  const getDefaultSheet = useCallback(
    (fileName: string) => {
      const sheetName = extractSheetNameFromFileName(fileName);
      if (!sheetName) return null;
      // 紐付け可能なシートかを確認
      const sheet = sortedSheets.find(
        ({ displayName }) => displayName === sheetName
      );
      if (!sheet) return null;
      if (!isConnectableSheet(sheet)) return null;
      return sheet;
    },
    [sortedSheets, isConnectableSheet]
  );

  useEffect(() => {
    const uploadedFileIDList = uploadedFileList.map(({ id }) => id);

    const initialConnects: SheetCSVConnect[] = uploadedFileList
      .map(({ id: fileId, file }) => {
        const sheet = getDefaultSheet(file.name);
        if (!sheet) return null;
        return {
          fileId,
          sheetId: sheet.id,
          writeStatus: "waiting",
        };
      })
      .filter((x): x is SheetCSVConnect => x !== null);

    setCSVSheetConnectList((prev) => {
      // 1) まだ残っているファイルの項目だけ残す
      const kept = prev.filter((item) =>
        uploadedFileIDList.includes(item.fileId)
      );

      // 2) 新規に追加すべき項目だけピックアップ
      const toAdd = initialConnects.filter(
        (init) => !kept.some((item) => item.fileId === init.fileId)
      );

      return [...kept, ...toAdd];
    });
  }, [uploadedFileList, sortedSheets]);

  const runUpload = useCallback(async () => {
    // ref: [レートリミット: どの1秒間(ウィンドウ)についても「ウィンドウのコスト」が6を超えないこと。](https://www.notion.so/algo-artis/SSUI-API-2025-07-07ver-184af951024b807897a6cf64f1f2fb91)
    // 任意の1秒間での累計コスト ≤ 6 を遵守するために、
    // maxConcurrentApiCount件ずつ並列実行し、バッチ間に1秒待機してAPIサーバーへの負荷を平準化します。
    const maxConcurrentApiCount = getMaxConcurrentApiCount(
      "postFileVersionDataCSV"
    );

    // 全てのファイルを削除不可にする
    setUploadedFileList((prev) =>
      prev.map(({ id, file }) => ({ id, file, isDisableDelete: true }))
    );

    // バッチ処理でAPIを実行
    for (
      let i = 0;
      i < csvSheetConnectList.length;
      i += maxConcurrentApiCount
    ) {
      const batch = csvSheetConnectList.slice(i, i + maxConcurrentApiCount);

      // 現在のバッチのアイテムをin_progressに設定
      setCSVSheetConnectList((prev) =>
        prev.map((item) =>
          batch.some((batchItem) => batchItem.fileId === item.fileId)
            ? { ...item, writeStatus: "in_progress" }
            : item
        )
      );

      // 現在のバッチのAPIを並列実行
      const promises = batch.map(async ({ fileId, sheetId }) => {
        const { file } = uploadedFileList.find(({ id }) => id === fileId) ?? {};
        if (file) {
          try {
            await postFileVersionDataCSV({
              organizationId,
              versionId,
              file,
              sheetId,
              saveType,
            });
            setCSVSheetConnectList((prev) =>
              prev.map((item) =>
                item.fileId === fileId
                  ? { ...item, writeStatus: "success" }
                  : item
              )
            );
          } catch (error) {
            const { errorMessage } = getAPIErrorDetailOrMessage(error);
            setCSVSheetConnectList((prev) =>
              prev.map((item) =>
                item.fileId === fileId
                  ? {
                      ...item,
                      writeStatus: "failed",
                      error: errorMessage,
                    }
                  : item
              )
            );
            throw error;
          }
        }
      });

      // 現在のバッチの全てのAPIのレスポンスを待つ
      await Promise.allSettled(promises);

      // 最後のバッチ以外は1秒待機
      if (i + maxConcurrentApiCount < csvSheetConnectList.length) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  }, [
    uploadedFileList,
    csvSheetConnectList,
    organizationId,
    versionId,
    saveType,
  ]);

  const isDisablePrimaryButton =
    uploadedFileList.length === 0 ||
    csvSheetConnectList.length === 0 ||
    csvSheetConnectList.length !== uploadedFileList.length ||
    csvSheetConnectList.some(({ writeStatus }) => writeStatus !== "waiting");

  const isDisableCancelButton = csvSheetConnectList.some(
    ({ writeStatus }) => writeStatus === "in_progress"
  );

  const getExtElement = (id: string) => {
    const fileData = csvSheetConnectList.find((file) => file.fileId === id);
    const status = fileData?.writeStatus ?? "waiting";
    const errorMessage = fileData?.error ?? "";

    return <OperationStatusLabel status={status} message={errorMessage} />;
  };

  return {
    runUpload,
    isDisablePrimaryButton,
    isDisableCancelButton,
    csvSheetConnectList,
    setCSVSheetConnectList,
    getExtElement,
  };
};
